import { usePackageBookingStore } from "@elixir/stores";
import {
  Appointment,
  AppointmentChips,
  AppointmentChipType,
  AppointmentEndStatus,
  CHECKED_IN_STATUS,
  MODES_OF_APT,
  PACKAGE_POPUP_TYPE,
  PackageInclusion,
  SUBJECT_KEYS,
} from "@elixir/types";
import { sendMrLink } from "apis";
import dateFormat from "dateformat";
import { useMemo } from "react";
import {
  DoctorChipUi,
  EkaPassTagChipUi,
  GenericAppointmentChipIconUi,
  NewPatientChipUi,
  OverlayedTagChipContainerUi,
  PackageChipUi,
  RecordChipComponentUi,
  RequestCallbackChipComponenetUi,
  RequestCallbackStripComponentUi,
  StatusChipUi,
  TeleStatusChipUi,
} from "ui";
import { QUEUE_TYPE } from "../../../constants/appointments";
import HprHfrFlowPitchCard from "../../../organisms/hpr-hfr-onboarding/persuasions/dhis-pitch-card";
import RightPanelCardBottomActionButtons from "../../../organisms/queue/right-panel-card-bottom-action-buttons";
import ShowConfigSelectedColumns from "../../../organisms/queue/show-config-selected-columns";
import useQueueStore from "../../../stores/queue-store";
import {
  initMedicalCertificate,
  isAllowed,
  onAddVitalsAction,
  onAppointmentAction,
  onOpenTreatmentPlan,
  onUploadMedicalRecordEvent,
  openCreateAbhaFlow,
} from "../../../utils/bridges/methods";
import { track } from "../../../utils/tracking";
import UserData from "./user-data";
import { useMyAccessProfileV2 } from "../../../apis/tryst/get-my-access-profile";
import { getQueueTypeFromStatus } from "../../../utils/appointment-utils";
import { ReactComponent as HouseChimenyRegularRegularIcon } from "../../../assets/icons/font-awesome/house-chimney-medical-regular.svg";
import useGetViewShowToken from "../../../hooks/use-get-view-show-token";
import { getAclViewClassName } from "@elixir/utils";

const RightPanelCard = ({
  emptyState = false,
  apt,
  checkedInStatus,
  className = "",
  isHighlighted = false,
}: {
  emptyState?: boolean;
  apt: Appointment;
  checkedInStatus?: CHECKED_IN_STATUS;
  className?: string;
  isHighlighted?: boolean;
}) => {
  const { token, visit_end, discount_conf, treatmentPlans } = apt || {};
  const rightPanelQueueType = useQueueStore(
    (state) => state.rightPanelQueueType
  );
  const viewType = useQueueStore((state) => state.viewType);
  const clinicId = useQueueStore((state) => state.clinicId);

  const setPackagePopupType = usePackageBookingStore(
    (state) => state.setPackagePopupType
  );
  const setSelectedAppointment = usePackageBookingStore(
    (state) => state.setSelectedAppointment
  );
  const setSelectedPatient = usePackageBookingStore(
    (state) => state.setSelectedPatient
  );

  const queueType =
    rightPanelQueueType === QUEUE_TYPE.COMPLETED ||
      rightPanelQueueType === QUEUE_TYPE.CHECKED_IN
      ? rightPanelQueueType
      : getQueueTypeFromStatus(apt.status);

  const handlePackageChipClick = () => {
    track("handle_package_chip_click");
    setPackagePopupType(PACKAGE_POPUP_TYPE.CURRENT_TREATMENT);
    setSelectedAppointment(apt);
    setSelectedPatient(apt.patient);
  };

  const { data: myAccessProfile } = useMyAccessProfileV2();

  const doctorOfApt = useMemo(() => {
    return myAccessProfile?.doctors?.find(
      (item) => item.oid === apt.doctor_oid
    );
  }, [apt.doctor_oid, myAccessProfile?.doctors]);

  const clinicHfrId = useMemo(() => {
    return myAccessProfile?.clinics?.find((item) => item.id === apt.clinic_id)
      ?.abdm?.hfr_id;
  }, [apt.clinic_id, myAccessProfile?.clinics]);

  const isTeleApt = useMemo(() => {
    const isTeleApt = apt.mode === MODES_OF_APT.TELE;
    return isTeleApt;
  }, [apt]);

  const pkg = (
    (apt?.package_inclusions?.length || 0) > 0
      ? apt?.package_inclusions?.[0]
      : {}
  ) as PackageInclusion;

  const showToken = useGetViewShowToken();
  const toShowToken =
    rightPanelQueueType === QUEUE_TYPE.CHECKED_IN || showToken;

  const isPadAccessAllowed = isAllowed(SUBJECT_KEYS.PAD_ACCESS);

  return (
    <div className={`dr-flex dr-flex-col dr-w-full ${className} dr-group`}>
      <div
        className={`dr-flex dr-items-center dr-justify-start dr-space-x-8 ${rightPanelQueueType === QUEUE_TYPE.COMPLETED
            ? "dr-px-78"
            : toShowToken
              ? "dr-px-56"
              : "dr-px-15"
          }`}
      >
        {clinicId === "all" ? (
          <OverlayedTagChipContainerUi
            containerClassName={`bg-darwin-neutral-100 dr-flex dr-flex-row dr-items-center`}
          >
            <HouseChimenyRegularRegularIcon className="dr-min-w-16 dr-w-16 dr-h-16" />
            <div className="CalloutRegular dr-ml-4 dr-text-darwin-neutral-1000">
              {
                myAccessProfile?.clinics.find((t) => t.id === apt.clinic_id)
                  ?.name
              }
            </div>
          </OverlayedTagChipContainerUi>
        ) : null}
        {!!myAccessProfile?.doctors?.length &&
          myAccessProfile.doctors.length > 1 ? (
          <DoctorChipUi
            name={doctorOfApt?.name as string}
            pic={doctorOfApt?.pic}
          />
        ) : null}
        {checkedInStatus ? (
          <StatusChipUi checkedInStatus={checkedInStatus} />
        ) : null}
        {apt.new_patient ? <NewPatientChipUi /> : null}
        {discount_conf ? (
          <EkaPassTagChipUi
            discount={discount_conf.discount}
            discountType={
              discount_conf.discount_type === "P" ? "percent" : "flat"
            }
            patientName={apt.patient.fln}
          />
        ) : null}
        {isTeleApt ? <TeleStatusChipUi /> : null}
        {apt?.callback_requested && !AppointmentEndStatus.has(apt.status) ? (
          <RequestCallbackChipComponenetUi />
        ) : null}
        <RecordChipComponentUi
          addVitalsAndLabResults={() => {
            onAddVitalsAction(apt);
          }}
          initMedicalCertificate={() => {
            initMedicalCertificate(apt);
          }}
          requestMedicalRecords={async () => {
            await sendMrLink({ patientOid: apt.patient_oid });
          }}
          uploadMedicalRecords={() => {
            onUploadMedicalRecordEvent(apt.patient_oid, { source: "file" });
          }}
          apt={apt}
          onClickLinkAbha={() => {
            openCreateAbhaFlow(apt);
          }}
          isHfrCreated={!!clinicHfrId}
          onboardOntoHprHfrUi={<HprHfrFlowPitchCard clinicId={apt.clinic_id} />}
        />
        {apt.chips?.map((chip) => {
          switch (chip.type) {
            case AppointmentChipType.PACKAGES: {
              return (
                <PackageChipUi
                  key={chip.type}
                  onClick={handlePackageChipClick}
                  name={chip.cta}
                  className={getAclViewClassName(SUBJECT_KEYS.TREATMENT)}
                />
              );
            }
            case AppointmentChipType.DENTAL_PACKAGES: {
              const { type } = chip;
              return (
                <PackageChipUi
                  key={type}
                  onClick={() => onOpenTreatmentPlan(apt)}
                  name={chip.cta}
                />
              );
            }
            default: {
              const chipData = chip as AppointmentChips;
              return (
                <GenericAppointmentChipIconUi key={chipData.type} chip={chip} />
              );
            }
          }
        })}
      </div>

      <div className="dr-flex dr-items-center dr-space-x-8 dr-w-full">
        {rightPanelQueueType === QUEUE_TYPE.COMPLETED ? (
          <div className="HeadingsH2Bold dr-flex dr-flex-col dr-px-8 dr-items-start dr-text-doctor-darwin-neutral-1000 dr-self-start dr-my-18">
            <div className="">
              {visit_end && dateFormat(new Date(visit_end as string), "hh:MM")}
            </div>
            <div className="BodyBody2Regular">
              {visit_end && dateFormat(new Date(visit_end as string), "TT")}
            </div>
          </div>
        ) : null}
        {toShowToken ? (
          <div className="HeadingsH2Bold space-x-8 flex items-center dr-my-18 dr-px-8 self-start">
            {/* <BarRegularIcon className="dr-text-doctor-darwin-neutral-500" /> */}
            <div>{token?.toString().padStart(2, "0")}</div>
          </div>
        ) : null}
        <div
          className={`dr-flex-1 dr-bg-darwin-touch-neutral-0 dr-rounded-8 dr-border-1 dr-border-solid dr-border-darwin-touch-neutral-100 hover:dr-shadow-elevation-200 dr-transition-shadow ${isHighlighted ? "dr-shadow-elevation-200" : ""
            }`}
        >
          {apt?.callback_requested && !AppointmentEndStatus.has(apt.status) ? (
            <RequestCallbackStripComponentUi
              apt={apt}
              onClick={() => {
                onAppointmentAction({
                  a: apt,
                  cId: apt.clinic_id || clinicId,
                  dontAllowStartVisit: true,
                  openVideoCallWindow: true,
                  isNotesMode: !isPadAccessAllowed,
                });
              }}
            />
          ) : (
            <></>
          )}
          <div className="dr-p-16 dr-space-y-8">
            <div className="flex item-center justify-between dr-flex dr-gap-y-12 dr-gap-x-16">
              <UserData apt={apt} className="" />
              <ShowConfigSelectedColumns apt={apt} queueType={queueType} />
              {viewType === "compact" ? (
                <RightPanelCardBottomActionButtons
                  emptyState={emptyState}
                  apt={apt}
                />
              ) : null}
            </div>
            {viewType === "default" ? (
              <div className="dr-gap-x-16">
                <RightPanelCardBottomActionButtons
                  emptyState={emptyState}
                  apt={apt}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RightPanelCard;
